import React from "react";
import { getRole } from "../../services/authenticationService";
import RepairItem from "./RepairItem";

type RepairItemData = {
	repairId: number;
	cat_Id: number;
	updatedOn: string;
	updatedBy: string;
	isResolved: boolean;
	isUpdateable: boolean;
	category: string;
	subCategory: string;
	remedy: string;
	repairTypes?: string[];
	selectedRepairType?: string;
	repairedOn?: string;
	description?: string;
};

type RepairData = {
	repairType: string;
	repairDate: Date | null;
	description: string;
	repairId?: number;
};

type RecommendationData = {
	high: RepairItemData[];
	medium: RepairItemData[];
	low: RepairItemData[];
};

interface CategoryGroupProps {
	data: RecommendationData;
	repairData: { [key: number]: RepairData };
	editingRepairId: number | null;
	onStatusChange: (isResolved: boolean, cat_Id: number, repairId: number, repairTypes?: string[]) => void;
	onRepairTypeChange: (cat_Id: number, repairType: string) => void;
	onRepairDateChange: (cat_Id: number, date: Date | null) => void;
	onRepairDescriptionChange: (cat_Id: number, description: string) => void;
	onSaveRepair: (cat_Id: number) => void;
	onCancelEdit: (cat_Id: number) => void;
	onStartEdit: (cat_Id: number, repairId: number, selectedRepairType: string, repairedOn: string, description: string, repairTypes: string[]) => void;
	formatDate: (dateString: string) => string;
}

const PRIORITY_CONFIG = {
	high: { backgroundColor: "#FDBBBB", testId: "high-checkbox" },
	medium: { backgroundColor: "#FFE8C0", testId: "medium-checkbox" },
	low: { backgroundColor: "#FFFFA3", testId: "low-checkbox" }
} as const;

const CategoryGroup: React.FC<CategoryGroupProps> = ({
	data,
	repairData,
	editingRepairId,
	onStatusChange,
	onRepairTypeChange,
	onRepairDateChange,
	onRepairDescriptionChange,
	onSaveRepair,
	onCancelEdit,
	onStartEdit,
	formatDate,
}) => {
	const isViewer = getRole() === 'Viewer';
	const hasRecommendations = data.high?.length > 0 || data.medium?.length > 0 || data.low?.length > 0;

	const renderRepairItems = (items: RepairItemData[], priorityLevel: keyof typeof PRIORITY_CONFIG) => {
		if (!items || items.length === 0) return null;

		const config = PRIORITY_CONFIG[priorityLevel];

		return items.map((value, i) => {
			if (isViewer || !value.isUpdateable) {
				return (
					<div
						key={i}
						className="form-controls mb-2"
						id="form-controls"
						style={{ backgroundColor: config.backgroundColor }}
					>
						<div className="label">{value.category}</div>
						<div className="label">{value.subCategory}</div>
						<div className="form-value">{value.remedy}</div>
						<div className="pb-1" />
					</div>
				);
			}

			return (
				<RepairItem
					key={i}
					item={value}
					backgroundColor={config.backgroundColor}
					priorityLevel={priorityLevel}
					testId={config.testId}
					repairData={repairData[value.cat_Id]}
					editingRepairId={editingRepairId}
					onStatusChange={onStatusChange}
					onRepairTypeChange={onRepairTypeChange}
					onRepairDateChange={onRepairDateChange}
					onRepairDescriptionChange={onRepairDescriptionChange}
					onSaveRepair={onSaveRepair}
					onCancelEdit={onCancelEdit}
					onStartEdit={onStartEdit}
					formatDate={formatDate}
				/>
			);
		});
	};

	return (
		<fieldset className="recommendation border rounded mt-2" id="fildset-2">
			<legend className="legend-text float-none w-auto">RECOMMENDATIONS</legend>
			<div className="form mx-2 mb-1" id="form">
				{hasRecommendations ? (
					<>
						{renderRepairItems(data.high || [], 'high')}
						{renderRepairItems(data.medium || [], 'medium')}
						{renderRepairItems(data.low || [], 'low')}
					</>
				) : (
					<div className="alert alert-warning mt-2" role="alert" data-testid="alert-1">
						There is no issue against this pole.
					</div>
				)}
			</div>
		</fieldset>
	);
};

export default CategoryGroup;

